/**
 * Brain organ textures — idle and activated.
 *
 * A top-down brain (two hemispheres + central fissure + a maze of gyri/sulci)
 * suspended in green preservative liquid inside a glass jar. Metal lid + base
 * bands. Scattered gold flecks float in the liquid.
 *
 * 3 cells = 150x150 px.
 */
import Phaser from 'phaser';
import { GRID_SIZE } from '../../grid';
import type { HasTexture } from './core';

export function registerBrainTextures(scene: Phaser.Scene, has: HasTexture): void {
  if (!has('organ-brain-idle')) makeBrain(scene, 'organ-brain-idle', false);
  if (!has('organ-brain-active')) makeBrain(scene, 'organ-brain-active', true);
}

function makeBrain(scene: Phaser.Scene, key: string, active: boolean): void {
  const cells = 3;
  const size = cells * GRID_SIZE; // 150
  const g = scene.add.graphics();

  // ---------- Glass jar ----------
  const jarPad = 5;
  const jarX = jarPad;
  const jarY = jarPad;
  const jarW = size - 2 * jarPad;
  const jarH = size - 2 * jarPad;
  const jarR = 18;

  // Faint glass body (translucent gray under the liquid — gives the rim a
  // subtle thickness vs. just a colored rectangle).
  g.fillStyle(0xdcdcdc, 0.35);
  g.fillRoundedRect(jarX, jarY, jarW, jarH, jarR);

  // Green preservative liquid — inset slightly so a thin glass rim shows.
  const liqInset = 3;
  const liqColor = active ? 0xb0e088 : 0x9fd478;
  g.fillStyle(liqColor, active ? 0.92 : 0.88);
  g.fillRoundedRect(
    jarX + liqInset,
    jarY + liqInset,
    jarW - 2 * liqInset,
    jarH - 2 * liqInset,
    jarR - 2,
  );

  // Subtle liquid shading (darker at the bottom — looks heavier).
  g.fillStyle(0x6fa848, 0.22);
  g.fillRoundedRect(
    jarX + liqInset,
    jarY + jarH * 0.55,
    jarW - 2 * liqInset,
    jarH * 0.45 - liqInset,
    jarR - 2,
  );

  // Glass outline
  g.lineStyle(2.5, 0xb8b8b8, 1);
  g.strokeRoundedRect(jarX, jarY, jarW, jarH, jarR);

  // Glass highlight stripe (vertical white sheen near left edge).
  g.lineStyle(3, 0xffffff, 0.45);
  g.beginPath();
  g.moveTo(jarX + 9, jarY + 20);
  g.lineTo(jarX + 9, jarY + jarH - 28);
  g.strokePath();
  // Smaller secondary highlight on the right edge
  g.lineStyle(1.5, 0xffffff, 0.3);
  g.beginPath();
  g.moveTo(jarX + jarW - 8, jarY + 30);
  g.lineTo(jarX + jarW - 8, jarY + jarH * 0.5);
  g.strokePath();

  // ---------- Metal lid (top) ----------
  const bandH = 14;
  const bandInset = -3; // bulges slightly past the jar sides
  drawMetalBand(g, jarX + bandInset, jarY - 5, jarW - 2 * bandInset, bandH);
  // small neck nub at the top center
  g.fillStyle(0x8a8a8a, 1);
  g.fillRoundedRect(size / 2 - 12, jarY - 9, 24, 6, 2);
  g.lineStyle(1, 0x5a5a5a, 1);
  g.strokeRoundedRect(size / 2 - 12, jarY - 9, 24, 6, 2);

  // ---------- Metal base (bottom) ----------
  drawMetalBand(g, jarX + bandInset, jarY + jarH - bandH + 5, jarW - 2 * bandInset, bandH);
  // small base nub at the bottom center (mirror of the top)
  g.fillStyle(0x8a8a8a, 1);
  g.fillRoundedRect(size / 2 - 12, jarY + jarH + 3, 24, 6, 2);
  g.lineStyle(1, 0x5a5a5a, 1);
  g.strokeRoundedRect(size / 2 - 12, jarY + jarH + 3, 24, 6, 2);

  // ---------- Brain ----------
  const cx = size / 2;
  const cy = size / 2 + 2;
  const brainColor = active ? 0xd6909c : 0xc88090; // pink-tan
  const outlineColor = active ? 0x6b2a36 : 0x5a2230;
  const sulciColor = active ? 0x8e3a4a : 0x7a3040; // dark grooves

  const brainW = size * 0.66;
  const brainH = size * 0.74;

  // Soft drop-shadow under the brain so it pops off the green
  g.fillStyle(0x000000, 0.18);
  g.fillEllipse(cx + 1, cy + 4, brainW + 2, brainH + 2);

  // Brain body
  g.fillStyle(brainColor, 1);
  g.lineStyle(2.5, outlineColor, 1);
  g.fillEllipse(cx, cy, brainW, brainH);
  g.strokeEllipse(cx, cy, brainW, brainH);

  // Central fissure (slightly wavy vertical line)
  g.lineStyle(2.2, outlineColor, 1);
  g.beginPath();
  const fissureTop = cy - brainH * 0.46;
  const fissureBot = cy + brainH * 0.46;
  const fSteps = 14;
  for (let i = 0; i <= fSteps; i++) {
    const t = i / fSteps;
    const y = fissureTop + (fissureBot - fissureTop) * t;
    const x = cx + Math.sin(t * Math.PI * 2.5) * 1.4;
    if (i === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  g.strokePath();

  // Gyri/sulci pattern — C-shaped curves on each hemisphere opening medially
  drawGyri(g, cx, cy, brainW, brainH, -1, sulciColor);
  drawGyri(g, cx, cy, brainW, brainH, +1, sulciColor);

  // Short, broken cross-cuts on each hemisphere (don't cross the fissure).
  // These add the "maze" feel of real cortical folds.
  g.lineStyle(1.2, sulciColor, 0.6);
  const crossSeed = 1234; // deterministic pseudo-random
  const rand = mulberry32(crossSeed);
  for (const side of [-1, 1] as const) {
    for (let i = 0; i < 6; i++) {
      const yFrac = -0.32 + (i / 5) * 0.62 + (rand() - 0.5) * 0.05;
      const y = cy + brainH * yFrac;
      const xStart = side === -1 ? cx - brainW * (0.05 + rand() * 0.1) : cx + brainW * 0.05;
      const xEnd =
        side === -1 ? cx - brainW * (0.32 + rand() * 0.1) : cx + brainW * (0.32 + rand() * 0.1);
      const steps = 10;
      g.beginPath();
      for (let s = 0; s <= steps; s++) {
        const tt = s / steps;
        const x = xStart + (xEnd - xStart) * tt;
        const yy = y + Math.sin(tt * Math.PI * 3 + i) * 1.8;
        if (s === 0) g.moveTo(x, yy);
        else g.lineTo(x, yy);
      }
      g.strokePath();
    }
  }

  // ---------- Gold flecks in the liquid (and a few on the brain) ----------
  // These mimic the reference image's scattered yellow highlights.
  const fleckColor = active ? 0xfff088 : 0xe8c060;
  const fleckAlpha = active ? 1 : 0.95;
  g.fillStyle(fleckColor, fleckAlpha);
  const flecks: [number, number, number][] = [
    // [x, y, radius]
    [cx - brainW * 0.32, cy - brainH * 0.22, 2.2],
    [cx + brainW * 0.30, cy - brainH * 0.06, 2.4],
    [cx - brainW * 0.10, cy + brainH * 0.30, 2.0],
    [cx + brainW * 0.34, cy + brainH * 0.22, 2.5],
    [cx + brainW * 0.04, cy - brainH * 0.34, 1.8],
    // Floating in the liquid (outside the brain)
    [jarX + 14, jarY + 24, 1.8],
    [jarX + jarW - 14, jarY + jarH - 36, 2.0],
    [cx - brainW * 0.55, cy + brainH * 0.05, 1.8],
    [cx + brainW * 0.55, cy + brainH * 0.40, 1.6],
  ];
  for (const [fx, fy, fr] of flecks) {
    g.fillCircle(fx, fy, fr);
  }
  // Extra-bright twinkle highlights when active
  if (active) {
    g.fillStyle(0xffffff, 0.9);
    g.fillCircle(cx - brainW * 0.32 - 0.6, cy - brainH * 0.22 - 0.6, 0.8);
    g.fillCircle(cx + brainW * 0.30 - 0.6, cy - brainH * 0.06 - 0.6, 0.9);
    g.fillCircle(cx + brainW * 0.34 - 0.6, cy + brainH * 0.22 - 0.6, 0.9);
  }

  g.generateTexture(key, size, size);
  g.destroy();
}

/**
 * Draws a metal lid/base band with a brushed-steel look (highlight strip on
 * top edge, slight shadow on bottom).
 */
function drawMetalBand(g: Phaser.GameObjects.Graphics, x: number, y: number, w: number, h: number): void {
  // Body
  g.fillStyle(0x9b9b9b, 1);
  g.fillRoundedRect(x, y, w, h, 4);
  // Top highlight
  g.fillStyle(0xc4c4c4, 1);
  g.fillRoundedRect(x + 2, y + 1, w - 4, 3, 2);
  // Bottom shadow
  g.fillStyle(0x6c6c6c, 1);
  g.fillRoundedRect(x + 2, y + h - 4, w - 4, 3, 2);
  // Outline
  g.lineStyle(1.5, 0x4a4a4a, 1);
  g.strokeRoundedRect(x, y, w, h, 4);
}

/**
 * Draws a stack of irregular nested C-shaped curves on one hemisphere,
 * simulating gyri. sideMul = -1 for left hemisphere (C opens medially),
 * sideMul = +1 for right hemisphere. Strong per-curve jitter so the layers
 * don't read as concentric tree rings.
 */
function drawGyri(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  brainW: number,
  brainH: number,
  sideMul: 1 | -1,
  color: number,
): void {
  g.lineStyle(1.6, color, 0.9);
  const hcx = cx + sideMul * brainW * 0.20; // hemisphere center
  const layers = 6;
  const seed = sideMul === -1 ? 8101 : 4242;
  const rand = mulberry32(seed);
  for (let L = 0; L < layers; L++) {
    const shrink = 0.95 - L * 0.14;
    const rx = brainW * 0.32 * shrink;
    const ry = brainH * 0.42 * shrink;
    g.beginPath();
    const steps = 28;
    for (let s = 0; s <= steps; s++) {
      const tt = s / steps;
      // sweep the outer half-ellipse from -π/2 to +π/2
      const a = -Math.PI / 2 + Math.PI * tt;
      const baseX = Math.cos(a) * rx;
      const baseY = Math.sin(a) * ry;
      // Per-curve wobble: composite of two sines + a touch of randomness
      const wobble =
        Math.sin(tt * Math.PI * 5 + L * 1.7) * 2.2 +
        Math.sin(tt * Math.PI * 11 + L * 0.7) * 1.1 +
        (rand() - 0.5) * 0.8;
      // wobble pushes outward along the local radial direction
      const radLen = Math.hypot(baseX, baseY) || 1;
      const nx = (baseX / radLen) * wobble;
      const ny = (baseY / radLen) * wobble;
      const px = hcx + sideMul * (baseX + nx);
      const py = cy + baseY + ny;
      if (s === 0) g.moveTo(px, py);
      else g.lineTo(px, py);
    }
    g.strokePath();
  }
  // Short radial sulci cutting in from the brain edge, irregular spacing
  g.lineStyle(1.3, color, 0.7);
  for (let i = 0; i < 4; i++) {
    const baseA = -Math.PI / 2 + (Math.PI * (i + 0.5)) / 4;
    const a = baseA + (rand() - 0.5) * 0.25;
    const r1 = brainW * (0.18 + rand() * 0.08);
    const r2 = brainW * (0.34 + rand() * 0.04);
    const x1 = hcx + sideMul * Math.cos(a) * r1;
    const y1 = cy + Math.sin(a) * (brainH * 0.42);
    const x2 = hcx + sideMul * Math.cos(a) * r2;
    const y2 = cy + Math.sin(a) * (brainH * 0.44);
    g.beginPath();
    g.moveTo(x1, y1);
    g.lineTo(x2, y2);
    g.strokePath();
  }
}

/**
 * Tiny seeded PRNG so the gyri pattern is deterministic per-texture (no
 * regenerating differently on each game start).
 */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
